import { AxiosResponse } from 'axios';
import { Action } from 'redux';

import { SpaceType } from '~src/spacesPlatform/state/SpaceInfo';

import {
  ClearDraftsState,
  DraftFetchByParentIdFailed,
  DraftFetchByParentIdRequested,
  DraftFetchByParentIdSucceeded,
  DraftsFetchInfoFailed,
  DraftsFetchInfoRequested,
  DraftsFetchInfoSucceeded,
  DraftsInfoAvailable} from '../../bootstrap/redux/actions/AppActionTypes';
import { isActionFactory } from '../../bootstrap/redux/actions/isActionFactory';
import { GetDraftsByIdRequestParams,GetDraftsRequestParams } from '../requests/DraftsRequests';
import { ContentDraftStatus } from '../serverApi/types/ContentDraft';
import { Draft, DraftContent, DraftsById, SpringResponse } from '../types/DraftsTypes';

export interface FetchDraftsSucceededAction extends Action {
  drafts: Draft[];
}

export interface DraftsInfoAvailableAction extends Action {
  contents: DraftContent[];
}

export interface FetchDraftsFailedAction extends Action {
  error: AxiosResponse<SpringResponse>;
}

export interface ClearDraftsStateAction extends Action { }

export interface FetchDraftsRequestedAction extends Action {
  data: GetDraftsRequestParams;
  spaceType: SpaceType;
  description: string;
  drafts: Draft[];
}

export interface FetchDraftByParentIdSucceededAction extends Action {
  draft: Draft;
}

export interface FetchDraftByParentIdFailedAction extends Action {
  error: AxiosResponse<SpringResponse>;
}

export interface FetchDraftByParentIdRequestedAction extends Action {
  spaceType: SpaceType;
  parentLuid: string;
  data: GetDraftsByIdRequestParams;
}

export function clearDraftState(): ClearDraftsStateAction {
  return {
    type: ClearDraftsState
  };
}

export function fetchDraftsRequested(spaceType: SpaceType, projectLuid?: string): FetchDraftsRequestedAction {
  return {
    type: DraftsFetchInfoRequested,
    data: { projectLuid },
    spaceType: spaceType,
    description: 'Drafts',
    drafts: [],
  };
}

export function fetchDraftsSucceeded(result: Draft[]): FetchDraftsSucceededAction {
  return {
    type: DraftsFetchInfoSucceeded,
    drafts: result,
  };
}

export function fetchDraftsFailed(error: AxiosResponse<SpringResponse>): FetchDraftsFailedAction {
  return {
    type: DraftsFetchInfoFailed,
    error,
  };
}

export function fetchDraftByParentIdRequested(spaceType: SpaceType, parentLuid: string, parentRevisionId?: string): FetchDraftByParentIdRequestedAction {
  return {
    type: DraftFetchByParentIdRequested,
    spaceType,
    parentLuid,
    data: { parentRevisionId }
  };
}

export function fetchDraftByParentIdSucceeded(draft: Draft): FetchDraftByParentIdSucceededAction {
  return {
    type: DraftFetchByParentIdSucceeded,
    draft,
  };
}

export function fetchDraftByParentIdFailed(error: AxiosResponse<SpringResponse>): FetchDraftByParentIdFailedAction {
  return {
    type: DraftFetchByParentIdFailed,
    error
  };
}

export function draftsAvailable(result: Draft[]): DraftsInfoAvailableAction {
  return {
    type: DraftsInfoAvailable,
    contents: convertDraftsArrayToDraftsContent(result),
  };
}

export function draftsAvailableInState(result: DraftsById): DraftsInfoAvailableAction {
  return {
    type: DraftsInfoAvailable,
    contents: convertDraftsStateToDraftsContent(result),
  };
}

function convertDraftsArrayToDraftsContent(drafts: Draft[]): DraftContent[] {
  let draftContentList: DraftContent[] = [];

  drafts.forEach(draft => {
    const { parentContentLuid, draftLuid } = draft;
    draftContentList.push({
      draftLuid,
      draftStatus: getDraftStatus(parentContentLuid),
      parentContentLuid,
    });
  });

  return draftContentList;
}

function convertDraftsStateToDraftsContent(drafts: DraftsById): DraftContent[] {
  let draftContentList: DraftContent[] = [];
  for (let [draftLuid, draftData] of Object.entries(drafts)) {
    const { parentContentLuid } = draftData;
    draftContentList.push({
      draftLuid,
      draftStatus: getDraftStatus(parentContentLuid),
      parentContentLuid,
    });
  }

  return draftContentList;
}

function getDraftStatus(parentContentLuid: string): string {
  return parentContentLuid ? ContentDraftStatus.unpublishedchanges : ContentDraftStatus.unpublished;
}

export const isFetchDraftsRequestedAction = isActionFactory<FetchDraftsRequestedAction>(DraftsFetchInfoRequested);
export const isFetchDraftsSucceededAction = isActionFactory<FetchDraftsSucceededAction>(DraftsFetchInfoSucceeded);
export const isFetchDraftsFailedAction = isActionFactory<FetchDraftsFailedAction>(DraftsFetchInfoFailed);

export const isClearDraftsStateAction = isActionFactory<ClearDraftsStateAction>(ClearDraftsState);
export const isFetchDraftByParentIdFailedAction = isActionFactory<FetchDraftByParentIdFailedAction>(DraftFetchByParentIdFailed);
export const isFetchDraftByParentIdSucceededAction = isActionFactory<FetchDraftByParentIdSucceededAction>(DraftFetchByParentIdSucceeded);
export const isFetchDraftByParentIdRequestedAction = isActionFactory<FetchDraftByParentIdRequestedAction>(DraftFetchByParentIdRequested);
export const isDraftsInfoAvailable = isActionFactory<DraftsInfoAvailableAction>(DraftsInfoAvailable);
